import { useCallback, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { useCan } from '@discovr/common';
import { useCampaignCapabilitiesStore } from '@discovr/features';
import { SCOPES } from '@discovr/types';
import { canWriteAtLevel } from '../../../lib/campaignPermissions';
import type { PendingConfirm } from './OutreachConfirmDialog';
import { useSuppressionMutations } from './useSuppressionMutations';

/**
 * The CRM row menu's Suppress / Unsuppress. Suppressions are outreach:manage
 * on the BE (admin tier), and a read-only grant on this campaign may not
 * change anything here, so both must hold for the menu to offer them.
 * Suppress goes through the Inbox's own confirm dialog; unsuppress is
 * immediate, as it is in the Inbox.
 *
 * `suppression` keeps one identity while its inputs hold, so the table columns
 * built from it stay memoised and the open row menu is not remounted.
 */
export function useCrmSuppression(campaignId: string) {
  const accessLevel = useCampaignCapabilitiesStore((s) => s.accessLevel);
  const hasWriteScope = useCan(SCOPES.outreachWrite);
  const hasManageScope = useCan(SCOPES.outreachManage);
  const canManage = hasWriteScope && hasManageScope && canWriteAtLevel(accessLevel);
  const { suppress, unsuppress } = useSuppressionMutations(campaignId);
  const [pending, setPending] = useState<PendingConfirm | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const requestSuppress = useCallback((entryId: string, email: string) => {
    setPending({ kind: 'suppress', entryId, email });
    setConfirmOpen(true);
  }, []);
  const { mutate: unsuppressById } = unsuppress;
  const unsuppressRow = useCallback((suppressionId: number) => unsuppressById(suppressionId), [unsuppressById]);

  const suppression = useMemo(
    () => ({ canManage, requestSuppress, unsuppress: unsuppressRow }),
    [canManage, requestSuppress, unsuppressRow],
  );

  const confirm = () => {
    setConfirmOpen(false);
    if (pending?.kind !== 'suppress') return;
    const { email } = pending;
    suppress.mutate(email, { onSuccess: () => toast.success(`Suppressed ${email}`) });
  };

  return {
    suppression,
    dialog: {
      open: confirmOpen,
      pending,
      busy: suppress.isPending,
      onClose: () => setConfirmOpen(false),
      onConfirm: confirm,
    },
  };
}

export type CrmSuppression = ReturnType<typeof useCrmSuppression>['suppression'];
